import { useEffect, useRef, useState } from 'react';

import TermsCheckbox from '../../../components/TermsCheckbox';
import GradientButton from '../../../components/GradientButton';
import WatchVideoButton from '../../../components/WatchVideoButton';
import Icon from '../../../components/ui/Icon/Icon';

import '../../../components/css/Modals.css';

interface InterviewTipsModalProps {
  isOpen: boolean;
  onClose: () => void;
  termsAccepted: boolean;
  onTermsChange: (accepted: boolean) => void;
  onStartInterview: () => void;
  onWatchVideo: () => void;
  tips: string[];
  tipsHeading: string;
  legalText: {
    text: string;
    text2: string;
    terms: { text: string; url: string };
    privacy: { text: string; url: string };
  };
}

type TipIcon = { type: 'fa'; value: string } | { type: 'svg'; value: string };

const TIP_ICONS: TipIcon[] = [
  { type: 'fa', value: 'far fa-video' },
  { type: 'fa', value: 'far fa-clock' },
  { type: 'svg', value: '/svgs/sparkle.svg' },
  { type: 'fa', value: 'far fa-circle-check' },
];

const InterviewTipsModal = ({
  isOpen,
  onClose,
  termsAccepted,
  onTermsChange,
  onStartInterview,
  onWatchVideo,
  tips,
  tipsHeading,
  legalText,
}: InterviewTipsModalProps) => {
  const termsErrorRef = useRef<HTMLDivElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const [shakeButton, setShakeButton] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsModalOpen(true);
      setIsClosing(false);
    }
  }, [isOpen]);

  const handleClose = (callback?: () => void) => {
    setIsClosing(true);
    setTimeout(() => {
      setIsModalOpen(false);
      onClose();
      if (callback) callback();
    }, 300);
  };

  const handleGetStarted = () => {
    if (!termsAccepted) {
      setShowTermsError(true);
      setShakeButton(true);
      setTimeout(() => setShakeButton(false), 500);
      setTimeout(() => termsErrorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
      return;
    }
    setShowTermsError(false);
    onStartInterview();
  };

  const handleTermsChange = (accepted: boolean) => {
    onTermsChange(accepted);
    if (accepted) setShowTermsError(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) setShowTermsError(false);
  }, [isOpen]);

  if (!isModalOpen) return null;

  return (
    <div
      className={`modal-backdrop ${isClosing ? 'modal-fadeOut' : ''}`}
      onClick={() => handleClose()}
    >
      <div
        className={`modal-dialog ${isClosing ? 'modal-fadeOut' : ''}`}
        style={{ maxWidth: '500px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content">
          <div className="modal-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.2rem 1.25rem' }}>
            <h5 className="modal-title" style={{ fontWeight: 500, margin: 0, fontSize: '1.1rem' }}>
              Interview Preparation Tips!
            </h5>
            <Icon icon="fal fa-xmark" style={{ fontSize: '1.1rem', color: '#495057', cursor: 'pointer' }} onClick={() => handleClose()} ariaLabel="Close" />
          </div>

          <div className="modal-body" style={{ padding: '1rem 1.25rem 1.25rem' }}>
            <p style={{
              fontWeight: 500,
              fontSize: '1rem',
              color: '#1a1a2e',
              margin: '0 0 0.85rem 0',
            }}>
              {tipsHeading}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '1.7rem' }}>
              {tips.map((tip, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1rem',
                    fontWeight: 400,
                    // border: '1px solid #DEE2E6',
                    backgroundColor: '#F2F3F7',
                    borderRadius: '12px',
                    fontSize: '0.85rem',
                    color: '#495057',
                  }}
                >
                  {(TIP_ICONS[i]?.type === 'svg') ? (
                    <img
                      src={TIP_ICONS[i].value}
                      alt="Tip Icon"
                      draggable={false}
                      style={{ width: '1.1rem', height: '1.1rem', flexShrink: 0, userSelect: 'none' }}
                    />
                  ) : (
                    <i
                      className={TIP_ICONS[i]?.value || 'fal fa-circle'}
                      style={{ fontSize: '1.2rem', color: '#2F86DD', flexShrink: 0, width: '1.1rem', textAlign: 'center' }}
                    ></i>
                  )}
                  {tip}
                </div>
              ))}
            </div>

            <TermsCheckbox
              checked={termsAccepted}
              onChange={handleTermsChange}
              legalText={legalText}
              id="tipsTermsAgreement"
              compact
            />

            <GradientButton
              onClick={handleGetStarted}
              label="Get Started"
              shake={shakeButton}
              compact
            />

            <WatchVideoButton
              onClick={() => handleClose(() => onWatchVideo())}
              compact
              id="click-explainervideo-tips"
            />

            {showTermsError && (
              <div
                ref={termsErrorRef}
                style={{ color: '#802932', fontSize: '0.8rem', marginTop: '0.25rem' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <i className="fal fa-circle-exclamation" style={{ fontSize: '1rem', color: '#802932', marginRight: '0.2rem' }}></i>
                  Please confirm your acceptance of the terms and conditions by checking the box to continue with the interview.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewTipsModal;
